/** The delivery channels the SaaS notifications service supports. */
export const NOTIFICATION_CHANNELS = ['inApp', 'email'] as const;

export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

/** A row in the header bell tray. `readAt === null` means unread. */
export interface InAppNotification {
    id: string;
    title: string;
    message: string;
    createdAt: string;
    readAt: string | null;
    ctaLabel: string | null;
    ctaTarget: string | null;
    templateType: string;
    category: string;
    /** Ties the notification back to the job(s) that produced it; the tray joins on this. */
    correlationId: string | null;
}

/**
 * A channel's resolved state for one notification type. `allowed` and
 * `userConfigurable` come from the delivery policy, so the UI can disable a
 * toggle the user is not permitted to change rather than letting them flip it
 * and watch the save get rejected.
 */
export interface EffectiveChannelState {
    enabled: boolean;
    allowed: boolean;
    userConfigurable: boolean;
    stored: boolean;
}

export type NotificationOptionComponent = 'select' | 'number' | 'switch' | 'text';

export interface NotificationOptionChoice {
    value: string;
    label: string;
}

/**
 * A tunable on a notification type — a threshold, a lead time, a cadence.
 *
 * The server sends the *definition* alongside the value, so the control is
 * rendered from the API rather than hardcoded here: adding an option to a
 * notification type, or changing its choices, needs no web change. `value` is
 * always a string whatever the component is, matching how it is stored.
 */
export interface EffectiveNotificationOption {
    id: string;
    label: string;
    description: string;
    component: NotificationOptionComponent;
    value: string;
    defaultValue: string;
    choices: NotificationOptionChoice[];
    min?: number;
    max?: number;
    /** True when a stored value is in force rather than the authored default. */
    customized: boolean;
}

/**
 * One notification type as the Settings screen sees it: its name, description,
 * per-channel state, its tunables, and the opt-out constraints its policy
 * imposes.
 */
export interface EffectiveNotificationPreference {
    templateType: string;
    templateName: string;
    description: string;
    category: string;
    channels: Record<NotificationChannel, EffectiveChannelState>;
    options: EffectiveNotificationOption[];
    userCanFullyOptOut: boolean;
    requireAtLeastOneChannel: boolean;
}

/**
 * An objection to a preference change — `preference` for a channel refused by
 * delivery policy, `option` for a value the notification type does not permit.
 * The service answers 200 with `valid: false` and these issues rather than a
 * 4xx, because the request was well-formed; the UI needs the reason to explain
 * why (e.g. security alerts cannot be silenced entirely).
 */
export interface PreferenceIssue {
    code: string;
    message: string;
    domain: 'preference' | 'option';
    path?: string[];
}

export interface PreferenceUpdateResult {
    valid: boolean;
    issues?: PreferenceIssue[];
}

export type NotificationPreferencePatch = Partial<Record<NotificationChannel, boolean>> & {
    options?: Record<string, string>;
};

/**
 * One notification as it went out across the tenant — a single notify call,
 * collapsed across every channel it travelled on.
 *
 * `status` is `sent` when every channel succeeded, `failed` when none did, and
 * `partial` when some landed and some did not — a distinction worth keeping
 * rather than rounding to whichever reads better.
 */
export interface NotificationActivityEntry {
    correlationId: string;
    sentAt: string;
    templateType: string;
    category: string;
    title: string;
    channels: string[];
    recipients: number;
    status: string;
    error: string | null;
}

export interface NotificationActivityPage {
    entries: NotificationActivityEntry[];
    total: number;
}

/** Lifecycle state of a tracked background job. `running` is in-flight; the rest are terminal. */
export type JobRunStatus = 'running' | 'succeeded' | 'failed' | 'partial' | 'canceled';

export const TERMINAL_JOB_STATUSES: readonly JobRunStatus[] = ['succeeded', 'failed', 'partial', 'canceled'];

export function isTerminalJob(status: JobRunStatus): boolean {
    return TERMINAL_JOB_STATUSES.includes(status);
}

/**
 * One tracked background job as the tray sees it — a sync or other long-running
 * operation the signed-in user started. `progress`/`total` drive a determinate
 * bar when both are known; otherwise a running job shows an indeterminate spinner.
 */
export interface JobRunEntry {
    jobRunId: string;
    /** Ties the run to the notification(s) it produced; the tray joins on this. */
    correlationId: string | null;
    kind: string;
    status: JobRunStatus;
    userId: string | null;
    title: string | null;
    progress: number | null;
    total: number | null;
    message: string | null;
    error: string | null;
    startedAt: string;
    completedAt: string | null;
    updatedAt: string;
}

export interface JobRunPage {
    entries: JobRunEntry[];
    total: number;
}
